import { useSelector, useDispatch } from "react-redux";
import ContentContainer from "../../../templates/content-container/contentContainer";
import React, { useEffect, useState } from "react";
import Table from "../../../ui-components/table/table";
import { InboundPickupsFilter, InboundPickupsFilters } from "./filter/filter";
import PickupDetails from "../../common/pickup-details/pickupDetails";
import { useInfiniteQuery } from "@tanstack/react-query";
import { apiQueryAdapter } from "../../../app/data/apiQueryAdapter";
import { userSelector } from "../../../slices/user/userSlice";
import "./inboundPickups.scss";
import { getInboundPickupColumns } from "./getInboundPickupColumns";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import { getPickupDetails } from "../../../services/pickups";
import { getPickup, pickupDetailsSelector } from "../../../slices/pickup/pickupDetailsSlice";
import { PageResponseModel } from "../../../app/data/common/models";
import { PickupsResponseContentModel } from "../../../app/data/pickup/models";
import mixpanel from "mixpanel-browser";

export const InboundPickups = () => {
  const dispatch = useDispatch();
  const userState = useSelector(userSelector);
  useEffect(() => {
    mixpanel.track("page_viewed", { page: "outbound_list" });
  }, [userState.activeTerminal?.id]);
  const pickupDetailsState = useSelector(pickupDetailsSelector);
  const [filters, setFilters] = useState<InboundPickupsFilters>({});
  const [pickupDetailsNumber, setPickupDetailsNumber] = useState<string>("");

  const {
    data: inboundData,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    error,
    isLoading,
    isPreviousData
  } = useInfiniteQuery(
    ['pickups', userState.activeTerminal?.id!, filters],
    async ({ pageParam = 0 }) => {
      return await apiQueryAdapter.query<PageResponseModel<PickupsResponseContentModel>>("/pickups/inbound", "GET", {
        params: {
          page: pageParam,
          originTerminalCode: userState.activeTerminal?.id,
          destinationTerminalCode: filters.d,
          statuses: !!filters.s ? filters.s.join(",") : ""
        },
      });
    },
    {
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage) =>
        !lastPage.data.last ? lastPage.data.pageable.pageNumber + 1 : null,
        keepPreviousData: true,
    }
  );

  const onSearch = (newFilters: any) => {
    setPickupDetailsNumber("");
    setFilters(newFilters);
  };

  const onShowPickupDetails = (pickupNumber: string) => {
    if (pickupDetailsNumber !== pickupNumber) {
      setPickupDetailsNumber(pickupNumber);
      dispatch(getPickup(pickupNumber));
    }
  };

  const pickupDetails = pickupDetailsState.pickup && getPickupDetails(pickupDetailsState.pickup);

  const tableData =
    inboundData?.pages.flatMap((page) => page.data.content) || [];

  const terminalAvailable = !!userState.activeTerminal?.id;

  return (
    <ContentContainer mods={{ "full-width": true }} title="Outbound Planning List">
      {(!terminalAvailable) && (
          <XGSErrorMessage>
            <div>
              You do not have assigned terminals.
              Email <a className="white-link" href="mailto:helpdesk@xgsi.com">helpdesk@xgsi.com</a> to request terminal assignment for your account.
            </div>
          </XGSErrorMessage>
      )}
      {isError && <XGSErrorMessage>{error}</XGSErrorMessage>}
      {(!isError && terminalAvailable) && (
        <div className="xgs-inbound-pickups">
          <InboundPickupsFilter isLoading={isFetching} onSearch={onSearch}/>
          <Table
            columns={getInboundPickupColumns()}
            data={tableData}
            isLoading={isLoading || (isFetching && isPreviousData)}
            infiniteScroll
            cursorPointer
            onInfiniteScroll={() => fetchNextPage()}
            infiniteScrollHasNext={!inboundData?.pages.some(
              (page) => page.data.content.length !== 50
            )}
            keepTableOnLoading
            infiniteScrollLoading={isFetchingNextPage}
            rowHeight={56}
            responsive
            minTableHeight={320}
            onRowClicked={(row: any) => onShowPickupDetails(row.pickup?.pickupNumber)}
          />
        </div>
      )}
      <PickupDetails
        readonly
        isOpen={!!pickupDetailsNumber}
        pickup={pickupDetails}
        onClose={() => setPickupDetailsNumber("")}
        isLoading={pickupDetailsState.requestStarted && pickupDetailsState.requestCreator === "GET_PICKUP"}
        error={pickupDetailsState.requestCreator === "GET_PICKUP" ? pickupDetailsState.requestError : ""}
      />
    </ContentContainer>
  );
};
